import React from 'react';

import { alpha, useTheme } from '@mui/material/styles';

import { SwitchView } from './SwitchView';

import type { SwitchProperties } from './Switch.types';

export const Switch: React.FC<SwitchProperties> = ({
  showIcons = true,
  checked,
  defaultChecked = false,
  disabled = false,
  onChange,
  dataTest,
  ...rest
}) => {
  const theme = useTheme();
  const [internalChecked, setInternalChecked] = React.useState(defaultChecked);
  const [isHovered, setIsHovered] = React.useState(false);
  const [isPressed, setIsPressed] = React.useState(false);

  const isChecked = checked === undefined ? internalChecked : checked;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (checked === undefined) {
      setInternalChecked(event.target.checked);
    }
    onChange?.(event, event.target.checked);
  };

  const handleMouseEnter = () => setIsHovered(true);

  const handleMouseLeave = () => {
    setIsHovered(false);
    setIsPressed(false);
  };

  const handleMouseDown = () => setIsPressed(true);

  const handleMouseUp = () => setIsPressed(false);

  const getIconColor = (): string => {
    if (isChecked) {
      return theme.palette.mode === 'dark'
        ? theme.palette.primary.contrastText
        : theme.palette.primary.main;
    }
    return theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[200];
  };

  const getStateLayerColor = (): string => {
    if (disabled) return 'transparent';
    if (isPressed) {
      return alpha(
        isChecked ? theme.palette.primary.main : theme.palette.grey[500],
        0.12,
      );
    }
    if (isHovered) {
      return alpha(
        isChecked ? theme.palette.primary.main : theme.palette.grey[500],
        0.08,
      );
    }
    return 'transparent';
  };

  return (
    <SwitchView
      isChecked={isChecked}
      isDisabled={disabled}
      showIcons={showIcons}
      stateLayerColor={getStateLayerColor()}
      iconColor={getIconColor()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onChange={handleChange}
      inputProps={rest}
      dataTest={dataTest}
    />
  );
};
